// MemberAvatar — round avatar for a family_member row.
//
// Renders the global profile picture (profiles.avatar_url, set via
// the Settings sheet's 「上傳頭像」 flow) when `avatarUrl` is set,
// and falls back to the row's initials over the family_members
// gradient otherwise. Used across the modules that surface a
// member's identity (FamStatusView hero + per-member cards + mood
// feed + past-checkin history; FamTreeView tree grid + right-rail
// list + edit-sheet header; FamListView assignee list + add-picker;
// ModuleMembersView candidate preview).
//
// The avatar URL only flows from `useCurrentUser().avatarUrl` for
// the row that IS the signed-in user (matched by id). Other
// family members don't have a global avatar linked in the current
// data shape, so they fall through to the gradient+initials path.

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type MemberAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

const DIM: Record<MemberAvatarSize, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-7 w-7 text-[11px]",
  md: "h-8 w-8 text-xs",
  lg: "h-11 w-11 text-sm",
  xl: "h-14 w-14 text-base",
};

export function MemberAvatar({
  initials,
  color,
  avatarUrl,
  size = "md",
  rounded = "full",
  ariaHidden = true,
}: {
  /** 1-2 char glyph rendered over the gradient when no photo is set. */
  initials: string;
  /** Tailwind gradient utility class from family_members.avatar_color. */
  color: string;
  /** Public Storage URL of the user's profile picture. Renders an
   *  `<img>` over the gradient when set; key ties the React/HTML
   *  cache to the URL so changes are reflected immediately. */
  avatarUrl?: string | null;
  size?: MemberAvatarSize;
  /** "full" → circle (most surfaces), "lg" → squircle (hero cards). */
  rounded?: "full" | "lg";
  ariaHidden?: boolean;
}) {
  // Broken / expired Storage URLs (deleted object, revoked policy)
  // fall back to the initials tile instead of leaving an empty
  // gradient circle. Reset when the URL changes so a re-upload
  // recovers without a reload.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => setImgFailed(false), [avatarUrl]);

  if (avatarUrl && !imgFailed) {
    return (
      <span
        aria-hidden={ariaHidden}
        className={cn(
          "grid shrink-0 place-items-center overflow-hidden bg-gradient-to-br font-semibold text-white ring-1 ring-background",
          DIM[size],
          rounded === "full" ? "rounded-full" : "rounded-2xl",
        )}
      >
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          // Cache-bust: Storage returns long-lived cache headers;
          // a new upload would otherwise linger in the browser
          // cache. Tying the React key to the URL itself forces
          // a fresh fetch only when the URL changes.
          key={avatarUrl}
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden={ariaHidden}
      className={cn(
        "grid shrink-0 place-items-center bg-gradient-to-br font-semibold text-white",
        color,
        DIM[size],
        rounded === "full" ? "rounded-full" : "rounded-2xl",
      )}
    >
      {initials}
    </span>
  );
}
